import { useEffect, useSyncExternalStore } from "react";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Button, LoadingSpinner } from "@a2z/ui";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import { isSellerNativeApp } from "@/lib/native/runtime";
import {
  clearNativeMetaAuthorization, getNativeMetaStatus, markNativeMetaInterrupted,
  readNativeMetaPending, receiveNativeMetaCallback, subscribeNativeMeta,
} from "@/lib/native/meta-authorization";
import { translateStandalone } from "@/lib/i18n/standalone";
import { keys } from "@/lib/i18n/portal";

export function NativeMetaBootstrap() {
  const status = useSyncExternalStore(subscribeNativeMeta, getNativeMetaStatus);
  useEffect(() => {
    if (!isSellerNativeApp() || !status) return;
    const pending = readNativeMetaPending();
    if (!pending) return;
    const timer = window.setTimeout(markNativeMetaInterrupted, Math.max(0, pending.expiresAt - Date.now()) + 1);
    return () => window.clearTimeout(timer);
  }, [status]);
  useEffect(() => {
    if (!isSellerNativeApp()) return;
    let active = true;
    let generation = getPrivateSessionState().generation;
    const receive = (url: string) => {
      if (!active) return;
      const path = receiveNativeMetaCallback(url);
      if (!path) return;
      // The deep link returns to the app; dismiss the provider browser too.
      void Browser.close().catch(() => undefined);
      window.history.replaceState(window.history.state, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    };
    const urls = App.addListener("appUrlOpen", ({ url }) => receive(url));
    const resumed = App.addListener("appStateChange", ({ isActive }) => {
      if (active && isActive) markNativeMetaInterrupted();
    });
    const closed = Browser.addListener("browserFinished", () => {
      if (active) markNativeMetaInterrupted();
    });
    void App.getLaunchUrl().then((launch) => {
      if (launch?.url) receive(launch.url);
    }).catch(() => undefined);
    if (readNativeMetaPending()) markNativeMetaInterrupted();
    const unsubscribe = subscribePrivateSession(() => {
      const next = getPrivateSessionState().generation;
      if (next !== generation) {
        generation = next;
        clearNativeMetaAuthorization();
      }
    });
    return () => {
      active = false;
      unsubscribe();
      for (const handle of [urls, resumed, closed]) void handle.then((listener) => listener.remove());
    };
  }, []);
  if (!isSellerNativeApp() || !status) return null;
  return (
    <aside role="status" aria-busy={status === "pending" || undefined} className="fixed inset-x-3 bottom-4 z-[100] mx-auto max-w-lg space-y-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 shadow-lg">
      <p className="flex items-center gap-2 text-sm text-[color:var(--text-primary)]">
        {status === "pending" ? <LoadingSpinner /> : null}
        {translateStandalone(status === "restart" ? keys.meta.nativeRestart : keys.meta.nativePending)}
      </p>
      <Button variant="outline" size="touch" onClick={() => {
        clearNativeMetaAuthorization();
        void Browser.close().catch(() => undefined);
      }}>
        {translateStandalone(status === "restart" ? keys.common.close : keys.common.cancel)}
      </Button>
    </aside>
  );
}
